import { useState, useCallback } from 'react';
import { SubscriptionState } from '../types/subscription';
import { StripeInvoiceLine } from '@components/InvoiceTable';
import {
  getTierAndTermByPlanId,
  getUserSubscriptionInfo,
  isActionableStripeSubscription
} from '@lib/utils';
import { getProrationV2 } from '@api-service/checkout';
import fetchPlans from '@api-service/fetch-plans';

const INITIAL_SUBSCRIPTION_STATE: SubscriptionState = {
  newSubscriptionPrice: '',
  newSubscriptionTier: '',
  newSubscriptionTerm: '',
  credit: '',
  total: undefined,
  invoiceLines: [],
  tier: '',
  term: ''
};

export const useSubscriptionProration = (
  user: any,
  accessToken: string | null
) => {
  const [subscriptionState, setSubscriptionState] = useState<SubscriptionState>(
    INITIAL_SUBSCRIPTION_STATE
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  const resetState = useCallback(() => {
    setSubscriptionState(INITIAL_SUBSCRIPTION_STATE);
    setError('');
  }, []);

  const fetchProrations = useCallback(
    async (planId: string | string[] | undefined) => {
      if (!user || !accessToken) {
        return;
      }
      const subscription = getUserSubscriptionInfo(user);

      // FFH-2592: this used to require the claim to say `active`, which is
      // stricter than the API's own requirement for a proration preview.
      // Routing now sends members here on any paid Stripe signal, so the
      // stricter test would silently return and leave them on a permanent
      // spinner: the page renders its loader until a price arrives.
      if (!isActionableStripeSubscription(subscription) || !planId) {
        return;
      }

      try {
        setPending(true);
        setError('');

        const planDetails = getTierAndTermByPlanId(planId);
        if (!planDetails?.term) {
          throw new Error('Invalid plan ID');
        }

        const { tier } = planDetails;
        const term = planDetails.term;

        // No coupon on the preview: plan changes are charged undiscounted, so
        // the quote must be undiscounted too (FFH-2262).
        const [invoice, priceData] = await Promise.all([
          getProrationV2({
            accessToken,
            term,
            tier
          }),
          fetchPlans([`${tier}_${term}`])
        ]);

        if (invoice?.error) {
          throw new Error(invoice.error);
        }

        const newSubscriptionPrice = calculatePrice(priceData);
        const invoicePreview = invoice as {
          total?: number;
          lines?: { data?: StripeInvoiceLine[] };
        };

        setSubscriptionState({
          newSubscriptionPrice: newSubscriptionPrice.toString(),
          newSubscriptionTier: tier,
          newSubscriptionTerm: term,
          credit: '',
          total: invoicePreview.total,
          invoiceLines: invoicePreview.lines?.data || [],
          tier: subscription?.tier?.toLowerCase(),
          term: subscription?.term?.toLowerCase()
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch proration data';
        setError(errorMessage);
        console.error('Error fetching prorations:', err);
      } finally {
        setPending(false);
      }
    },
    [user, accessToken]
  );

  return {
    subscriptionState,
    pending,
    error,
    fetchProrations,
    resetState,
    setPending,
    setError
  };
};

const calculatePrice = (priceData: any): number => {
  const unitAmount = priceData?.data?.[0]?.unit_amount;
  return unitAmount ? unitAmount / 100 : 0;
};
